import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { Dropdown } from 'primereact/dropdown';
import { formatCurrency, formatNumberWithCommas, parseNumberFromFormatted } from '../../utils/helper';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoader, setIsLoader] = useState(false);
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('');
  
  // For creating new order
  // Order Details (Type, Priority, Payment, Ready Time)
  const [orderDetails, setOrderDetails] = useState({
      order_type: 'Local Order',
      priority: 'normal',
      urgent_surcharge: '',
      payment_method: 'cash',
      amount_paid: '',
      ready_at: '',
      waybill_number: ''
  });
  const currency = process.env.REACT_APP_CURRENCY || 'TZS';
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [unit, setUnit] = useState('piece');
    const [customPrice, setCustomPrice] = useState(''); // Allow price override
    const [cart, setCart] = useState([]);
  
  // Customer Selection
  const [customerType, setCustomerType] = useState('new'); // 'new' or 'existing'
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      shipping_address: '',
      date_of_birth: '',
      insurance_type_id: '',
      insurance_card_number: ''
  });
  const [phoneCode, setPhoneCode] = useState('+255');
  const [insuranceTypes, setInsuranceTypes] = useState([]);

  useEffect(() => {
    if (id) {
      getSingleData();
    } else {
        getProducts();
        getPatients();
        getInsuranceTypes();
    }
  }, [id]);

  useEffect(() => {
    const patientId = searchParams.get('patient_id');
    if (patientId && patients.length > 0) {
        const patient = patients.find(p => p.id == patientId);
        if (patient) {
            setCustomerType('existing');
            setSelectedPatient(patient.id);
            setCustomerInfo({
                customer_name: patient.name,
                customer_email: patient.email,
                customer_phone: patient.phone,
                shipping_address: patient.address || ''
            });
        }
    }
  }, [searchParams, patients]);

  const getProducts = async () => {
      try {
          const response = await ApiService.request({
              method: 'GET',
              url: 'admin/products',
              data: { per_page: 1000, status: 'in_stock' }
          });
          if (response.data.status) {
              setProducts(response.data.data.products || []);
          }
      } catch (error) {
          console.error(error);
      }
  };

  const getPatients = async () => {
    try {
        const response = await ApiService.request({
            method: "GET",
            url: "admin/patients", 
            data: { per_page: 1000 }
        });
        if (response.data.status) {
            setPatients(response.data.data.patients || []);
        }
    } catch (error) {
        console.error(error);
    }
  };

  const getInsuranceTypes = async () => {
    try {
        const response = await ApiService.request({
            method: "GET",
            url: "admin/insurance-types/all", 
        });
        if (response.data.status) {
            setInsuranceTypes(response.data.data || []);
        }
    } catch (error) {
        console.error(error);
    }
  };

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/orders/${id}`,
      });
      const data = response.data;
      if (data.status) {
        setOrder(data.data);
        setStatus(data.data.status);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load order data');
    } finally {
      setIsLoader(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'POST', // Or PUT
        url: `admin/orders/${id}`,
        data: { status },
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/orders');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setIsLoader(false);
    }
  };

  const handleCustomerChange = (e) => {
      const { name, value } = e.target;
      setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handlePatientSelect = (patientId) => {
      setSelectedPatient(patientId);
      const patient = patients.find(p => p.id === patientId);
      if (patient) {
          setCustomerInfo({
              customer_name: patient.name,
              customer_email: patient.email,
              customer_phone: patient.phone,
              shipping_address: patient.address || ''
          });
      }
  };

  // Update price when unit changes or product changes
    useEffect(() => {
        if (selectedProduct) {
            const product = products.find(p => p.id === selectedProduct);
            if (product) {
                let price = parseFloat(product.price);
                if (unit === 'pair') {
                    price *= 2;
                }
                setCustomPrice(price);
            }
        }
    }, [unit, selectedProduct]);

    const addToCart = () => {
        if (!selectedProduct) {
            toast.error("Please select a product");
            return;
        }
        
        const product = products.find(p => p.id === selectedProduct);
        if (!product) return;

        // Check stock availability based on unit
        const quantityToDeduct = unit === 'pair' ? quantity * 2 : quantity;

        if (quantityToDeduct > product.stock) {
              toast.error(`Only ${product.stock} pieces available. Required: ${quantityToDeduct}`);
              return;
        }

        // Use custom price if provided, otherwise default product price (adjusted for unit)
        let basePrice = parseFloat(product.price);
        if (unit === 'pair') basePrice *= 2;
        
        const finalPrice = customPrice !== '' ? parseFloat(customPrice) : basePrice;

        const existingItem = cart.find(item => item.product_id === product.id && item.unit === unit);
        if (existingItem) {
            const newQty = existingItem.quantity + parseInt(quantity);
            const newQtyToDeduct = unit === 'pair' ? newQty * 2 : newQty;

            if (newQtyToDeduct > product.stock) {
                toast.error(`Cannot add more. Max stock is ${product.stock} pieces`);
                return;
            }
            // Update price if custom price is set, or keep existing
            const updatedPrice = customPrice !== '' ? finalPrice : existingItem.price;
            setCart(cart.map(item => (item.product_id === product.id && item.unit === unit) ? { ...item, quantity: newQty, price: updatedPrice } : item));
        } else {
            setCart([...cart, {
                product_id: product.id,
                product_name: product.formatted_name,
                unit: unit,
                price: finalPrice,
                quantity: parseInt(quantity)
            }]);
        }
        
        setSelectedProduct(null);
        setQuantity(1);
        setUnit('piece');
        setCustomPrice('');
    };

  const removeFromCart = (index) => {
      const newCart = [...cart];
      newCart.splice(index, 1);
      setCart(newCart);
  };

  const calculateSubTotal = () => {
      return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
      let total = calculateSubTotal();
      if (orderDetails.priority === 'urgent') {
          total += parseFloat(orderDetails.urgent_surcharge || 0);
      }
      return total;
  };

  const handleOrderDetailsChange = (e) => {
      const { name, value } = e.target;
      let newValue = value;

      if (name === 'urgent_surcharge' || name === 'amount_paid') {
          newValue = parseNumberFromFormatted(newValue);
      }

      setOrderDetails(prev => ({ ...prev, [name]: newValue }));
  };

  const handleCreateOrder = async (e) => {
      e.preventDefault();
      if (cart.length === 0) {
          toast.error("Cart is empty");
          return;
      }

      setIsLoader(true);
      try {
          let finalPhone = customerInfo.customer_phone;
          if (customerType === 'new') {
              finalPhone = phoneCode + customerInfo.customer_phone;
          }

          const total = calculateTotal();
          let paymentStatus = 'pending';
          if (parseFloat(orderDetails.amount_paid) >= total) {
              paymentStatus = 'paid';
          } else if (parseFloat(orderDetails.amount_paid) > 0) {
              paymentStatus = 'partially_paid';
          }

          const payload = {
              ...customerInfo,
              customer_phone: finalPhone,
              ...(customerType === 'existing' && selectedPatient ? { patient_id: selectedPatient } : {}),
              ...orderDetails,
              items: cart,
              total_amount: total,
              payment_status: paymentStatus,
              status: 'pending' // Default status
          };

          const response = await ApiService.request({
              method: 'POST',
              url: 'admin/orders',
              data: payload
          });

          if (response.data.status) {
              toast.success(response.data.message);
              navigate('/orders');
          } else {
              toast.error(response.data.message);
          }
      } catch (error) {
          toast.error(error.response?.data?.message || 'Something went wrong');
      } finally {
          setIsLoader(false);
      }
  };

  // Prepare product options for dropdown
  const productOptions = products.map(p => ({
      label: `${p.formatted_name}`,
      value: p.id
  }));

  // Prepare patient options for dropdown
  const patientOptions = patients.map(p => ({
      label: `${p.name} (${p.phone})`,
      value: p.id
  }));

  const isSelectedProductLens = () => {
      if (!selectedProduct) return false;
      const product = products.find(p => p.id === selectedProduct);
      return product?.category?.name === 'Lens';
  };

  // CREATE ORDER VIEW
  if (!id) {
    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Create New Sale
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/orders" className="btn btn-sm fw-bold btn-secondary">
                            Back
                        </Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="row">
                        <div className="col-lg-8">
                             <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <h3 className="card-title">Order Items</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-5 align-items-end">
                                        <div className="col-md-5">
                                            <label className="form-label">Product</label>
                                            <Dropdown 
                                                value={selectedProduct} 
                                                options={productOptions} 
                                                onChange={(e) => {
                                                    setSelectedProduct(e.value);
                                                    const prod = products.find(p => p.id === e.value);
                                                    if (prod) {
                                                        // Price update is handled by useEffect
                                                        if (prod.category?.name !== 'Lens') {
                                                            setUnit('piece');
                                                        }
                                                    }
                                                }} 
                                                optionLabel="label" 
                                                optionValue="value" 
                                                filter 
                                                showClear 
                                                filterBy="label"
                                                placeholder="Select a Product"
                                                className="w-100" 
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">Unit</label>
                                            {isSelectedProductLens() ? (
                                                <Dropdown 
                                                    value={unit} 
                                                    options={[{label: 'Piece', value: 'piece'}, {label: 'Pair', value: 'pair'}]} 
                                                    onChange={(e) => setUnit(e.value)} 
                                                    className="w-100" 
                                                />
                                            ) : (
                                                <span className="form-control form-control-solid bg-light">Piece</span>
                                            )}
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">Price Override</label>
                                            <div className="input-group">
                                                <span className="input-group-text">{currency}</span>
                                                <input 
                                                    type="number" 
                                                    className="form-control form-control-solid"
                                                    min="0"
                                                    step="0.01"
                                                    value={customPrice}
                                                    onChange={(e) => setCustomPrice(e.target.value)}
                                                    placeholder="Price"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">Qty</label>
                                            <input 
                                                type="number" 
                                                className="form-control form-control-solid"
                                                min="1"
                                                value={quantity}
                                                onChange={(e) => setQuantity(e.target.value)}
                                                placeholder="Qty"
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <button type="button" onClick={addToCart} className="btn btn-base w-100">Add</button>
                                        </div>
                                    </div>
                                    
                                    <div className="table-responsive">
                                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                            <thead>
                                                <tr className="fw-bold text-muted">
                                                    <th className="min-w-150px">Product</th>
                                                    <th className="min-w-100px text-center">Unit</th>
                                                    <th className="min-w-100px text-end">Price</th>
                                                    <th className="min-w-100px text-end">Qty</th>
                                                    <th className="min-w-100px text-end">Total</th>
                                                    <th className="min-w-50px text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cart.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.product_name}</td>
                                                        <td className="text-center">{item.unit || 'piece'}</td>
                                                        <td className="text-end">{formatCurrency(item.price)}</td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatCurrency(item.price * item.quantity)}</td>
                                                        <td className="text-end">
                                                            <button onClick={() => removeFromCart(index)} className="btn btn-icon btn-sm btn-light-danger">
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {cart.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center text-muted">No items in cart</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr className="fw-bold fs-6">
                                                    <td colSpan="4" className="text-end">Subtotal:</td>
                                                    <td className="text-end text-dark">{formatCurrency(calculateSubTotal())}</td>
                                                    <td></td>
                                                </tr>
                                                {orderDetails.priority === 'urgent' && (
                                                    <tr className="fw-bold fs-6">
                                                        <td colSpan="4" className="text-end text-danger">Urgent Surcharge:</td>
                                                        <td className="text-end text-danger">{formatCurrency(orderDetails.urgent_surcharge)}</td>
                                                        <td></td>
                                                    </tr>
                                                )}
                                                <tr className="fw-bold fs-5 border-top border-gray-400">
                                                    <td colSpan="4" className="text-end">Total Amount:</td>
                                                    <td className="text-end text-primary">{formatCurrency(calculateTotal())}</td>
                                                    <td></td>
                                                </tr>
                                                <tr className="fw-bold fs-6 text-success">
                                                    <td colSpan="4" className="text-end">Amount Paid:</td>
                                                    <td className="text-end">{formatCurrency(orderDetails.amount_paid)}</td>
                                                    <td></td>
                                                </tr>
                                                <tr className="fw-bold fs-6 text-danger">
                                                    <td colSpan="4" className="text-end">Outstanding:</td>
                                                    <td className="text-end">{formatCurrency(calculateTotal() - orderDetails.amount_paid)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                             </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <h3 className="card-title">Customer Info</h3>
                                </div>
                                <div className="card-body">
                                    <div className="mb-5">
                                        <div className="d-flex gap-4 mb-3">
                                             <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    name="customerType"
                                                    id="existingCustomer"
                                                    checked={customerType === 'existing'} 
                                                    onChange={() => setCustomerType('existing')} 
                                                />
                                                <label className="form-check-label" htmlFor="existingCustomer">Existing Patient</label>
                                             </div>
                                             <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    name="customerType"
                                                    id="newCustomer"
                                                    checked={customerType === 'new'} 
                                                    onChange={() => {
                                                        setCustomerType('new');
                                                        setCustomerInfo({
                                                            customer_name: '',
                                                            customer_email: '',
                                                            customer_phone: '',
                                                            shipping_address: ''
                                                        });
                                                        setSelectedPatient(null);
                                                    }} 
                                                />
                                                <label className="form-check-label" htmlFor="newCustomer">New Customer</label>
                                             </div>
                                        </div>
                                        
                                        {customerType === 'existing' && (
                                            <div className="mb-3">
                                                <label className="form-label">Select Patient</label>
                                                 <Dropdown 
                                                    value={selectedPatient} 
                                                    options={patientOptions} 
                                                    onChange={(e) => handlePatientSelect(e.value)} 
                                                    optionLabel="label" 
                                                    optionValue="value" 
                                                    filter 
                                                    showClear 
                                                    filterBy="label"
                                                    placeholder="Select a Patient" 
                                                    className="w-100" 
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <form onSubmit={handleCreateOrder}>
                                        <div className="mb-3">
                                            <label className="form-label required">Name</label>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-solid" 
                                                name="customer_name"
                                                value={customerInfo.customer_name}
                                                onChange={handleCustomerChange}
                                                required
                                                readOnly={customerType === 'existing'}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Email</label>
                                            <input 
                                                type="email" 
                                                className="form-control form-control-solid" 
                                                name="customer_email"
                                                value={customerInfo.customer_email}
                                                onChange={handleCustomerChange}
                                                // readOnly={customerType === 'existing'}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label required">Phone</label>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-solid" 
                                                name="customer_phone"
                                                value={customerInfo.customer_phone}
                                                onChange={handleCustomerChange}
                                                required
                                                readOnly={customerType === 'existing'}
                                            />
                                        </div>
                                        
                                        {customerType === 'new' && (
                                            <>
                                                <div className="mb-3">
                                                    <label className="form-label">Date of Birth</label>
                                                    <input 
                                                        type="date" 
                                                        className="form-control form-control-solid" 
                                                        name="date_of_birth"
                                                        value={customerInfo.date_of_birth}
                                                        onChange={handleCustomerChange}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Insurance Type</label>
                                                    <select
                                                        className="form-select form-select-solid"
                                                        name="insurance_type_id"
                                                        value={customerInfo.insurance_type_id}
                                                        onChange={handleCustomerChange}
                                                    >
                                                        <option value="">Select Insurance</option>
                                                        {insuranceTypes.map((type) => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Insurance Card Number</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-solid" 
                                                        name="insurance_card_number"
                                                        value={customerInfo.insurance_card_number}
                                                        onChange={handleCustomerChange}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className="mb-3">
                                            <label className="form-label required">Address</label>
                                            <textarea 
                                                className="form-control form-control-solid" 
                                                name="shipping_address"
                                                rows="3"
                                                value={customerInfo.shipping_address}
                                                onChange={handleCustomerChange}
                                                readOnly={customerType === 'existing'}
                                                required
                                            ></textarea>
                                        </div>

                                        {/* <button type="submit" className="btn btn-base w-100" disabled={isLoader}>
                                            {isLoader ? 'Processing...' : 'Create Sale'}
                                        </button> */}
                                    </form>
                                </div>
                            </div>
                            <div className="card mb-5 mb-xl-10">
                                <div className="card-header">
                                    <h3 className="card-title">Order Details</h3>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label required">Order Type</label>
                                        <select 
                                            name="order_type" 
                                            className="form-select form-select-solid"
                                            value={orderDetails.order_type}
                                            onChange={handleOrderDetailsChange}
                                        >
                                            <option value="Local Order">Local Order</option>
                                            <option value="Special Order">Special Order</option>
                                            <option value="Waybill Order">Waybill Order</option>
                                        </select>
                                    </div>

                                    {orderDetails.order_type === 'Waybill Order' && (
                                        <div className="mb-3">
                                            <label className="form-label">Waybill Number</label>
                                            <input 
                                                type="text" 
                                                name="waybill_number" 
                                                className="form-control form-control-solid"
                                                value={orderDetails.waybill_number}
                                                onChange={handleOrderDetailsChange}
                                            />
                                        </div>
                                    )}
                                    
                                    <div className="mb-3">
                                        <label className="form-label required">Glazing Priority</label>
                                        <select 
                                            name="priority" 
                                            className="form-select form-select-solid"
                                            value={orderDetails.priority}
                                            onChange={handleOrderDetailsChange}
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>

                                    {orderDetails.priority === 'urgent' && (
                                        <div className="mb-3">
                                            <label className="form-label required text-danger">Urgent Surcharge</label>
                                            <div className="input-group">
                                                <span className="input-group-text">{currency}</span>
                                                <input 
                                                    type="text" 
                                                    name="urgent_surcharge"
                                                    className="form-control form-control-solid"
                                                    value={formatNumberWithCommas(orderDetails.urgent_surcharge)}
                                                    onChange={handleOrderDetailsChange}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label">Ready By</label>
                                        <input 
                                            type="datetime-local" 
                                            name="ready_at"
                                            className="form-control form-control-solid"
                                            value={orderDetails.ready_at}
                                            onChange={handleOrderDetailsChange}
                                        />
                                    </div>

                                    <div className="separator my-5"></div>

                                    <div className="mb-3">
                                        <label className="form-label required">Payment Method</label>
                                        <select 
                                            name="payment_method" 
                                            className="form-select form-select-solid"
                                            value={orderDetails.payment_method}
                                            onChange={handleOrderDetailsChange}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="card">Card</option>
                                            <option value="insurance">Insurance</option>
                                            <option value="mobile_money">Mobile Money</option>
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label required">Amount Paid</label>
                                        <div className="input-group">
                                            <span className="input-group-text">{currency}</span>
                                            <input 
                                                type="text" 
                                                name="amount_paid"
                                                className="form-control form-control-solid"
                                                value={formatNumberWithCommas(orderDetails.amount_paid)}
                                                onChange={handleOrderDetailsChange}
                                            />
                                        </div>
                                        <div className="form-text mt-2">
                                            Total: {formatCurrency(calculateTotal())} | Outstanding: <span className="text-danger fw-bold">{formatCurrency(calculateTotal() - (parseFloat(orderDetails.amount_paid || 0)))}</span>
                                        </div>
                                    </div>
                                    
                                    <button onClick={handleCreateOrder} className="btn btn-primary w-100 mt-3">
                                        <i className="fas fa-save me-2"></i> Create Order
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
  }

  // UPDATE ORDER VIEW (Simplified for now, as focus is on create)
  return (
    <div className="d-flex flex-column flex-column-fluid">
         <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                    <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                        Update Order
                    </h1>
                </div>
                <div className="d-flex align-items-center gap-2 gap-lg-3">
                    <Link to="/orders" className="btn btn-sm fw-bold btn-secondary">
                        Back
                    </Link>
                </div>
            </div>
        </div>
        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-fluid">
                 <div className="card mb-5 mb-xl-10">
                    <div className="card-header">
                        <h3 className="card-title">Order Details: #{order?.order_number}</h3>
                    </div>
                    <div className="card-body">
                        {isLoader ? <Loader /> : (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Status</label>
                                    <select 
                                        className="form-select form-select-solid"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="ready_not_collected">Ready NOT collected</option>
                                        <option value="completed">Complete (collected)</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <button type="submit" className="btn btn-base">Update Status</button>
                            </form>
                        )}
                    </div>
                 </div>
            </div>
        </div>
    </div>
  );
};

export default AddUpdate;
